import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import AdminLayout from "@/components/admin/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface AdminSetting {
  id: number
  key: string
  value: string
  label: string
  value_type: string
  updated_at: string
}

interface Props {
  settings: AdminSetting[]
}

export default function AdminSettingsIndex({ settings }: Props) {
  const [editing, setEditing] = useState<AdminSetting | null>(null)
  const [editValue, setEditValue] = useState("")

  function openEdit(setting: AdminSetting) {
    setEditing(setting)
    setEditValue(setting.value)
  }

  function handleSave() {
    if (!editing) return
    router.patch(`/admin/settings/${editing.id}`, {
      setting: { value: editValue },
    }, {
      onSuccess: () => setEditing(null),
    })
  }

  return (
    <AdminLayout>
      <Head title="Settings | Admin" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-semibold text-cream">Settings</h1>
          <p className="mt-1 text-sm text-cream/50">Global platform configuration</p>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Setting</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Key</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Value</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40">Type</th>
                <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40" />
              </tr>
            </thead>
            <tbody>
              {settings.map((setting) => (
                <tr key={setting.id} className="border-b border-white/[0.04] text-cream/70">
                  <td className="px-4 py-3 font-medium text-cream">{setting.label}</td>
                  <td className="px-4 py-3 font-mono text-xs text-cream/40">{setting.key}</td>
                  <td className="px-4 py-3 tabular-nums">{setting.value}</td>
                  <td className="px-4 py-3 text-xs text-cream/40">{setting.value_type}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-cream/50 hover:text-cream"
                      onClick={() => openEdit(setting)}
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-charcoal border-white/10 text-cream">
          <DialogHeader>
            <DialogTitle>Edit setting</DialogTitle>
            <DialogDescription className="text-cream/50">
              {editing?.label}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-cream/70">Value</Label>
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="mt-1 border-white/10 bg-white/5 text-cream"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="text-cream/50">
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}
